import type Model from "@ember-data/model";
import type Store from "@ember-data/store";
import { service } from "@ember/service";
import Creator from "parog-web/models/creator";
import JournalEvent from "parog-web/models/journal-event";
import type Medic from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";
import type SemeiaSessionService from "parog-web/services/session";

/**
 * This class is here to handle and share the concept of 'responsible'
 * which is saved as a 'responsibleId' and a 'responsibleType'.
 *
 * This is usefull for journal events that represent a planification like 'task-planned'
 * or 'consultation-planned'.
 *
 * It may be enriched by other behavior that they need to share
 */
export default class PlanifiedJournalEvent extends JournalEvent {
  @service declare store: Store;
  @service declare session: SemeiaSessionService;

  declare responsibleId: number | undefined;
  declare responsibleType: "medic" | "patient" | undefined;

  get hasResponsible(): boolean {
    return Boolean(this.responsibleType) && Boolean(this.responsibleId);
  }

  get currentUserIsResponsible(): boolean {
    if (!this.responsibleType) return false;
    const user = this.session[this.responsibleType.toLowerCase()];
    return user?.id == this.responsibleId;
  }

  get responsibleIsPatient(): boolean {
    return this.responsibleType == "patient";
  }

  /**
   * There is no network request done there.
   * The Creator should already been in the store otherwise it will return null.
   * So don't use this to check if the task has a responsible but use 'hasResponsible'
   */
  get responsible(): Medic | Patient | null {
    if (this.responsibleType != null && this.responsibleId != null) {
      const responsible = this.store.peekRecord(
        this.responsibleType,
        this.responsibleId
      );

      if (!responsible) {
        throw `No responsible found in the store with the type '${this.responsibleType}' corresponding to the id '${this.responsibleId}'`;
      }

      return responsible;
    }

    return null;
  }

  set responsible(creator) {
    if (!creator) {
      this.responsibleId = undefined;
      this.responsibleType = undefined;
    } else if (creator instanceof Creator) {
      this.responsibleId = parseInt(creator.id);
      this.responsibleType = (creator.constructor as typeof Model).modelName as
        | "medic"
        | "patient";
    } else {
      throw "You pass a responsible which is not of type 'Creator'.";
    }
  }

  isResponsible(creator: Creator | undefined): boolean {
    if (creator) {
      return (
        (creator.constructor as typeof Creator).modelName ==
          this.responsibleType && parseInt(creator.id) == this.responsibleId
      );
    }

    return false;
  }
}
